import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { useAxios } from '@/composables/useAxios'
import { handleAxiosError } from '@/composables/useAxiosError'
import { STATUS_CODES } from '@/constants'
import {
  AccountRelationship,
  AdminSubAccountListResponse,
  InviteDetailsResponse,
  ContextInfoResponse,
  SubAccountResourcesResponse
} from '@/types/subaccount'
import type {
  InviteSubAccountRequest,
  UpdateSubAccountRequest,
  AdminCreateSubAccountRequest,
  ContextSwitchRequest
} from '@/types/subaccount'
import { useJwtStore } from './jwt'

export const useSubAccountsStore = defineStore('subAccounts', () => {
  const children = ref<AccountRelationship[]>([])
  const parents = ref<AccountRelationship[]>([])
  const loading = ref(false)
  const childrenLoaded = ref(false)
  const parentsLoaded = ref(false)
  const resources = ref<SubAccountResourcesResponse | null>(null)
  const resourcesLoaded = ref(false)

  const isContextSwitched = computed(() => {
    const jwtStore = useJwtStore()
    const content = jwtStore.getJwtContent
    return !!content?.acting_as_account_id
  })

  const contextAccountId = computed(() => {
    const jwtStore = useJwtStore()
    return jwtStore.getJwtContent?.acting_as_account_id
  })

  const contextType = computed(() => {
    const jwtStore = useJwtStore()
    return jwtStore.getJwtContent?.acting_as_type
  })

  const originalAccountId = computed(() => {
    const jwtStore = useJwtStore()
    return jwtStore.getJwtContent?.original_account_id
  })

  async function fetchChildren(typeFilter?: string): Promise<void> {
    loading.value = true
    try {
      const url = typeFilter
        ? `/account/sub-accounts?type=${encodeURIComponent(typeFilter)}`
        : '/account/sub-accounts'
      const res = await useAxios<null, AccountRelationship[]>('GET', url, true)
      if (res?.status === STATUS_CODES.OK) {
        const validated = AccountRelationship.array().safeParse(res.data)
        if (validated.success) {
          children.value = validated.data
          childrenLoaded.value = true
        }
      }
    } catch (error) {
      handleAxiosError(error, { logPrefix: 'SubAccountsStore:fetchChildren' })
    } finally {
      loading.value = false
    }
  }

  async function fetchParents(): Promise<void> {
    loading.value = true
    try {
      const res = await useAxios<null, AccountRelationship[]>('GET', '/account/parents', true)
      if (res?.status === STATUS_CODES.OK) {
        const validated = AccountRelationship.array().safeParse(res.data)
        if (validated.success) {
          parents.value = validated.data
          parentsLoaded.value = true
        }
      }
    } catch (error) {
      handleAxiosError(error, { logPrefix: 'SubAccountsStore:fetchParents' })
    } finally {
      loading.value = false
    }
  }

  async function fetchResources(): Promise<void> {
    loading.value = true
    try {
      const res = await useAxios<null, SubAccountResourcesResponse>(
        'GET',
        '/account/sub-accounts/resources',
        true
      )
      if (res?.status === STATUS_CODES.OK) {
        const validated = SubAccountResourcesResponse.safeParse(res.data)
        if (validated.success) {
          resources.value = validated.data
          resourcesLoaded.value = true
        }
      }
    } catch (error) {
      handleAxiosError(error, { logPrefix: 'SubAccountsStore:fetchResources' })
    } finally {
      loading.value = false
    }
  }

  async function invite(req: InviteSubAccountRequest): Promise<AccountRelationship | null> {
    loading.value = true
    try {
      const res = await useAxios<InviteSubAccountRequest, AccountRelationship>(
        'POST',
        '/account/sub-accounts/invite',
        true,
        req
      )
      if (res?.status === STATUS_CODES.CREATED) {
        const validated = AccountRelationship.safeParse(res.data)
        if (validated.success) {
          children.value = [...children.value, validated.data]
          return validated.data
        }
      }
    } catch (error) {
      handleAxiosError(error, { logPrefix: 'SubAccountsStore:invite' })
      throw error
    } finally {
      loading.value = false
    }
    return null
  }

  async function updateRelationship(
    id: number,
    updates: UpdateSubAccountRequest
  ): Promise<AccountRelationship | null> {
    loading.value = true
    try {
      const res = await useAxios<UpdateSubAccountRequest, AccountRelationship>(
        'PATCH',
        `/account/sub-accounts/${id}`,
        true,
        updates
      )
      if (res?.status === STATUS_CODES.OK) {
        const validated = AccountRelationship.safeParse(res.data)
        if (validated.success) {
          children.value = children.value.map((c) => (c.id === id ? validated.data : c))
          return validated.data
        }
      }
    } catch (error) {
      handleAxiosError(error, { logPrefix: 'SubAccountsStore:updateRelationship' })
      throw error
    } finally {
      loading.value = false
    }
    return null
  }

  async function deactivate(id: number, reason?: string): Promise<AccountRelationship | null> {
    loading.value = true
    try {
      const res = await useAxios<{ reason?: string }, AccountRelationship>(
        'POST',
        `/account/sub-accounts/${id}/deactivate`,
        true,
        { reason }
      )
      if (res?.status === STATUS_CODES.OK) {
        const validated = AccountRelationship.safeParse(res.data)
        if (validated.success) {
          children.value = children.value.map((c) => (c.id === id ? validated.data : c))
          return validated.data
        }
      }
    } catch (error) {
      handleAxiosError(error, { logPrefix: 'SubAccountsStore:deactivate' })
      throw error
    } finally {
      loading.value = false
    }
    return null
  }

  async function reactivate(id: number): Promise<AccountRelationship | null> {
    loading.value = true
    try {
      const res = await useAxios<null, AccountRelationship>(
        'POST',
        `/account/sub-accounts/${id}/reactivate`,
        true
      )
      if (res?.status === STATUS_CODES.OK) {
        const validated = AccountRelationship.safeParse(res.data)
        if (validated.success) {
          children.value = children.value.map((c) => (c.id === id ? validated.data : c))
          return validated.data
        }
      }
    } catch (error) {
      handleAxiosError(error, { logPrefix: 'SubAccountsStore:reactivate' })
      throw error
    } finally {
      loading.value = false
    }
    return null
  }

  async function deleteRelationship(id: number): Promise<boolean> {
    loading.value = true
    try {
      const res = await useAxios<null, null>('DELETE', `/account/sub-accounts/${id}`, true)
      if (res?.status === STATUS_CODES.NO_CONTENT) {
        children.value = children.value.filter((c) => c.id !== id)
        return true
      }
    } catch (error) {
      handleAxiosError(error, { logPrefix: 'SubAccountsStore:deleteRelationship' })
      throw error
    } finally {
      loading.value = false
    }
    return false
  }

  async function getInviteDetails(token: string): Promise<InviteDetailsResponse | null> {
    try {
      const res = await useAxios<null, InviteDetailsResponse>(
        'GET',
        `/account/sub-accounts/invite/${encodeURIComponent(token)}`,
        false
      )
      if (res?.status === STATUS_CODES.OK) {
        const validated = InviteDetailsResponse.safeParse(res.data)
        if (validated.success) {
          return validated.data
        }
      }
    } catch (error) {
      handleAxiosError(error, { logPrefix: 'SubAccountsStore:getInviteDetails' })
      throw error
    }
    return null
  }

  async function acceptInvite(token: string): Promise<AccountRelationship | null> {
    loading.value = true
    try {
      const res = await useAxios<null, AccountRelationship>(
        'POST',
        `/account/sub-accounts/invite/${encodeURIComponent(token)}/accept`,
        true
      )
      if (res?.status === STATUS_CODES.OK) {
        const validated = AccountRelationship.safeParse(res.data)
        if (validated.success) {
          parentsLoaded.value = false
          return validated.data
        }
      }
    } catch (error) {
      handleAxiosError(error, { logPrefix: 'SubAccountsStore:acceptInvite' })
      throw error
    } finally {
      loading.value = false
    }
    return null
  }

  async function switchContext(req: ContextSwitchRequest): Promise<string | null> {
    loading.value = true
    try {
      const res = await useAxios<ContextSwitchRequest, { token: string }>(
        'POST',
        '/account/context/switch',
        true,
        req
      )
      if (res?.status === STATUS_CODES.OK && res.data?.token) {
        const jwtStore = useJwtStore()
        jwtStore.setJwt(res.data.token)
        return res.data.token
      }
    } catch (error) {
      handleAxiosError(error, { logPrefix: 'SubAccountsStore:switchContext' })
      throw error
    } finally {
      loading.value = false
    }
    return null
  }

  async function returnContext(): Promise<string | null> {
    loading.value = true
    try {
      const res = await useAxios<null, { token: string }>(
        'POST',
        '/account/context/return',
        true
      )
      if (res?.status === STATUS_CODES.OK && res.data?.token) {
        const jwtStore = useJwtStore()
        jwtStore.setJwt(res.data.token)
        return res.data.token
      }
    } catch (error) {
      handleAxiosError(error, { logPrefix: 'SubAccountsStore:returnContext' })
      throw error
    } finally {
      loading.value = false
    }
    return null
  }

  async function getContextInfo(): Promise<ContextInfoResponse | null> {
    try {
      const res = await useAxios<null, ContextInfoResponse>(
        'GET',
        '/account/context',
        true
      )
      if (res?.status === STATUS_CODES.OK) {
        const validated = ContextInfoResponse.safeParse(res.data)
        if (validated.success) {
          return validated.data
        }
      }
    } catch (error) {
      handleAxiosError(error, { logPrefix: 'SubAccountsStore:getContextInfo' })
    }
    return null
  }

  // Admin methods
  async function adminListSubAccounts(
    accountId: number,
    typeFilter?: string
  ): Promise<AdminSubAccountListResponse | null> {
    loading.value = true
    try {
      const url = typeFilter
        ? `/admin/account/${accountId}/sub-accounts?type=${encodeURIComponent(typeFilter)}`
        : `/admin/account/${accountId}/sub-accounts`
      const res = await useAxios<null, AdminSubAccountListResponse>('GET', url, true)
      if (res?.status === STATUS_CODES.OK) {
        const validated = AdminSubAccountListResponse.safeParse(res.data)
        if (validated.success) {
          return validated.data
        }
      }
    } catch (error) {
      handleAxiosError(error, { logPrefix: 'SubAccountsStore:adminListSubAccounts' })
      throw error
    } finally {
      loading.value = false
    }
    return null
  }

  async function adminCreateSubAccount(
    parentId: number,
    req: AdminCreateSubAccountRequest
  ): Promise<AccountRelationship | null> {
    loading.value = true
    try {
      const res = await useAxios<AdminCreateSubAccountRequest, AccountRelationship>(
        'POST',
        `/admin/account/${parentId}/sub-accounts`,
        true,
        req
      )
      if (res?.status === STATUS_CODES.CREATED) {
        const validated = AccountRelationship.safeParse(res.data)
        if (validated.success) {
          return validated.data
        }
      }
    } catch (error) {
      handleAxiosError(error, { logPrefix: 'SubAccountsStore:adminCreateSubAccount' })
      throw error
    } finally {
      loading.value = false
    }
    return null
  }

  async function adminUpdateSubAccount(
    parentId: number,
    relId: number,
    updates: UpdateSubAccountRequest
  ): Promise<AccountRelationship | null> {
    loading.value = true
    try {
      const res = await useAxios<UpdateSubAccountRequest, AccountRelationship>(
        'PATCH',
        `/admin/account/${parentId}/sub-accounts/${relId}`,
        true,
        updates
      )
      if (res?.status === STATUS_CODES.OK) {
        const validated = AccountRelationship.safeParse(res.data)
        if (validated.success) {
          return validated.data
        }
      }
    } catch (error) {
      handleAxiosError(error, { logPrefix: 'SubAccountsStore:adminUpdateSubAccount' })
      throw error
    } finally {
      loading.value = false
    }
    return null
  }

  async function adminDeleteSubAccount(parentId: number, relId: number): Promise<boolean> {
    loading.value = true
    try {
      const res = await useAxios<null, null>(
        'DELETE',
        `/admin/account/${parentId}/sub-accounts/${relId}`,
        true
      )
      if (res?.status === STATUS_CODES.NO_CONTENT) {
        return true
      }
    } catch (error) {
      handleAxiosError(error, { logPrefix: 'SubAccountsStore:adminDeleteSubAccount' })
      throw error
    } finally {
      loading.value = false
    }
    return false
  }

  function invalidateChildren() {
    childrenLoaded.value = false
  }

  function invalidateParents() {
    parentsLoaded.value = false
  }

  function invalidateResources() {
    resourcesLoaded.value = false
  }

  return {
    children,
    parents,
    loading,
    childrenLoaded,
    parentsLoaded,
    resources,
    resourcesLoaded,

    isContextSwitched,
    contextAccountId,
    contextType,
    originalAccountId,

    fetchChildren,
    fetchParents,
    fetchResources,
    invite,
    updateRelationship,
    deactivate,
    reactivate,
    deleteRelationship,
    getInviteDetails,
    acceptInvite,
    switchContext,
    returnContext,
    getContextInfo,

    adminListSubAccounts,
    adminCreateSubAccount,
    adminUpdateSubAccount,
    adminDeleteSubAccount,
    invalidateChildren,
    invalidateParents,
    invalidateResources
  }
})
